import { getCategorias } from "./getCategorias";
import { getClientes } from "./getClientes";
import { getContratos } from "./getContratos";
import { getIntermediarios } from "./getIntermediarios";
import { getOportunidades } from "./getOportunidades";
import { getOportunidadesBTL } from "./getOportunidadesBTL";
import { getSubcategorias } from "./getSubcategorias";
import { getUsers } from "./getUsers";
import { resolverNombreEjecutivo } from "./resolverNombreEjecutivo";

/**
 * Normaliza mes+año a "MM-YYYY". Mismo criterio que usan VentasDetalle (normalizeMonthLocal,
 * local a ese componente) y VentasMiListaClientesV2/CRM (su propio normalizeMonth, firma
 * distinta) para mes_cierre/mes_factura — centralizado acá para que la Pizarra no sume una
 * TERCERA copia (10.1/10.27). Detalle y CRM conservan sus propias copias sin tocar en esta
 * sesión (no son idénticas entre sí — ver docs/modulos/ventas-detalle.md). Exportada (2026-09-25)
 * para que Alertas de recurrencia comercial la reuse en vez de sumar una CUARTA copia — ver
 * docs/modulos/alertas-recurrencia-comercial.md.
 */
export const normalizeMonthLocal = (month, ano, createdAt, fallbackMonth = null) => {
  if (!month && !fallbackMonth) return null;
  const monthValue = month || fallbackMonth;
  const monthStr = monthValue.toString().trim();
  if (/^\d{2}-\d{4}$/.test(monthStr)) return monthStr;
  if (/^\d{4}-\d{2}-\d{2}$/.test(monthStr)) {
    return `${monthStr.slice(5, 7)}-${monthStr.slice(0, 4)}`;
  }
  const monthNum = parseInt(monthStr, 10);
  if (isNaN(monthNum) || monthNum < 1 || monthNum > 12) return null;
  const paddedMonth = monthNum.toString().padStart(2, "0");
  const year = ano ? ano.toString() : new Date(createdAt).getFullYear().toString();
  return `${paddedMonth}-${year}`;
};

/**
 * Carga + normaliza `sales` + `sales_contratos` + `sales_btl` para un país (10.127), en un
 * único array de oportunidades listo para consumo. Extraído para que la Pizarra no reimplemente
 * el patrón de carga/normalización que ya tienen (de forma similar pero NO idéntica)
 * VentasDetalle y VentasMiListaClientesV2/CRM — 10.1/10.27. Usado SOLO por la Pizarra por ahora;
 * Detalle/CRM no se migran en esta sesión (piden su propio provider aparte).
 *
 * @param {{pais: string}} params
 * @returns {Promise<{
 *   oportunidades: Array,
 *   clientes: Array,
 *   users: Array,
 *   intermediarios: Array,
 *   categorias: Array,
 *   subcategorias: Array,
 * }>}
 */
export const cargarOportunidadesVentas = async ({ pais }) => {
  const [
    clientesData,
    usersData,
    oportunidadesData,
    btlData,
    contratosData,
    intermediariosData,
    categorias,
    subcategorias,
  ] = await Promise.all([
    getClientes({ pais }),
    getUsers(),
    getOportunidades({ pais }),
    getOportunidadesBTL({ pais }),
    getContratos({ pais }),
    getIntermediarios({ pais }),
    getCategorias(),
    getSubcategorias(),
  ]);

  const mappedOportunidades = (oportunidadesData || [])
    .filter((o) => !o.eliminada)
    .map((oportunidad) => {
      const ejecutivoName =
        resolverNombreEjecutivo(
          oportunidad.ejecutivo,
          usersData,
          intermediariosData,
          oportunidad.client_id,
          clientesData,
          oportunidad.created_at,
          oportunidad.estatus,
          oportunidad.fecha_cierre,
          oportunidad.excluir_broker
        ) ?? (typeof oportunidad.ejecutivo === "string" ? oportunidad.ejecutivo.split(" ")[0] : "Sin asignar");

      const normalizedMesCierre = normalizeMonthLocal(
        oportunidad.mes_cierre,
        oportunidad.ano || oportunidad.ano_cierre,
        oportunidad.created_at,
        oportunidad.mes_cierre_old || oportunidad.mes_old
      );
      const normalizedMesFactura = normalizeMonthLocal(
        oportunidad.mes_factura,
        oportunidad.ano || oportunidad.ano_cierre,
        oportunidad.created_at,
        oportunidad.mes_old || oportunidad.mes_cierre_old
      );
      const anoOportunidad =
        normalizedMesCierre?.split("-")?.[1] ||
        normalizedMesFactura?.split("-")?.[1] ||
        new Date(oportunidad.created_at).getFullYear().toString();

      return {
        ...oportunidad,
        type: "sale",
        ejecutivoId: oportunidad.ejecutivo,
        ejecutivo: ejecutivoName,
        mes_cierre: normalizedMesCierre,
        mes_factura: normalizedMesFactura,
        estatus: oportunidad.estatus || (oportunidad.status ? `${oportunidad.status}%` : "0%"),
        ano: anoOportunidad,
      };
    });

  const mappedBTL = (btlData || [])
    .filter((btl) => !btl.eliminada && btl.pais === pais)
    .map((btl) => {
      const clienteEmpresa =
        clientesData.find((c) => String(c.id) === String(btl.client_id))?.empresa || "Cliente no encontrado";
      const ejecutivoName =
        resolverNombreEjecutivo(
          btl.executive_responsible || btl.created_by,
          usersData,
          intermediariosData,
          btl.client_id,
          clientesData,
          btl.created_at,
          btl.status ? `${btl.status}%` : "75%",
          btl.fecha_cierre,
          btl.excluir_broker
        ) ?? "Sin asignar";
      return {
        ...btl,
        id: btl.numeric_id.toString(),
        full_id: btl.id,
        type: "btl",
        cliente: clienteEmpresa,
        ejecutivo: ejecutivoName,
        ejecutivoId: btl.executive_responsible || btl.created_by,
        mes_cierre: normalizeMonthLocal(btl.mes_cierre, null, btl.created_at, null),
        mes_factura: normalizeMonthLocal(btl.mes_factura, null, btl.created_at, null),
        estatus: btl.status ? `${btl.status}%` : "75%",
        ano: btl.mes_cierre?.split("-")[1] || new Date(btl.created_at).getFullYear().toString(),
      };
    });

  const mappedContratos = (contratosData || [])
    .filter((c) => !c.eliminada && c.pais === pais)
    .map((c) => {
      const clienteEmpresa =
        clientesData.find((cl) => String(cl.id) === String(c.client_id))?.empresa || c.cliente || "Sin cliente";
      const ejecutivoName =
        resolverNombreEjecutivo(
          c.ejecutivo,
          usersData,
          intermediariosData,
          c.client_id,
          clientesData,
          c.created_at,
          c.estatus,
          c.fecha_cierre,
          c.excluir_broker
        ) ?? "Sin asignar";
      return {
        ...c,
        type: "contrato",
        cliente: clienteEmpresa,
        ejecutivo: ejecutivoName,
        ejecutivoId: c.ejecutivo,
        importe: c.importe_total,
        mes_cierre: normalizeMonthLocal(c.mes_cierre, c.ano, c.created_at, c.mes_inicio),
        mes_factura: c.mes_inicio,
        estatus: c.estatus || "50%",
        ano: c.ano?.toString() || c.mes_inicio?.split("-")[1] || new Date(c.created_at).getFullYear().toString(),
      };
    });

  return {
    oportunidades: [...mappedOportunidades, ...mappedBTL, ...mappedContratos],
    clientes: clientesData || [],
    users: usersData || [],
    intermediarios: intermediariosData || [],
    categorias: categorias || [],
    subcategorias: subcategorias || [],
  };
};
